/**
 * Sign-in (SSO) settings — Step 10's admin surface. Same conventions as Mail:
 * scope tabs (global default / org override), masked secret, draft-first,
 * and the Microsoft auto-configure wizard sharing the device-code machinery.
 * There is no test-send equivalent for SSO — the test IS signing in, so the
 * activate step hands you the sign-in URL to prove in a private window first.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Copy, ExternalLink, KeyRound, Wand2, XCircle } from 'lucide-react';
import { apiFetch, apiSend } from '../../lib/api';
import { useDirty } from '../../lib/dirty';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Modal } from '../../components/ui/modal';
import { cn } from '../../lib/utils';
import { qk } from '../../lib/queryClient';

interface SsoRow {
  entraTenantId: string | null;
  clientId: string | null;
  clientSecret: string | null;
  secretExpiresAt: number | null;
  isActive: boolean;
  /** Step 155 — the non-Microsoft door, unrelated to `isActive`. */
  agentLinkSignIn?: boolean;
}
interface SsoResponse {
  org: SsoRow | null;
  global?: SsoRow | null;
  effectiveScope: 'org' | 'global' | null;
  callbackUrl: string;
}
const EMPTY: SsoRow = {
  entraTenantId: null,
  clientId: null,
  clientSecret: null,
  secretExpiresAt: null,
  isActive: false,
  agentLinkSignIn: false,
};

interface StepResult {
  pass: boolean;
  label: string;
  error?: string;
}

/** Exported for the setup wizard (Step 165), which embeds the same flow
 *  rather than describing it. */
export function SsoWizard({
  scope,
  open,
  onClose,
}: {
  scope: 'org' | 'global';
  open: boolean;
  onClose: (done: boolean) => void;
}) {
  const queryClient = useQueryClient();
  type Step = 'signin' | 'app' | 'provision' | 'activate';
  const [step, setStep] = useState<Step>('signin');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [session, setSession] = useState<{
    sessionId: string;
    userCode: string;
    verificationUri: string;
  } | null>(null);
  const [tenantName, setTenantName] = useState('');
  // URL state on purpose: a step inside the provisioning wizard, not a view of
  // the settings page. Same reasoning as MailSetupWizard.
  const [mode, setMode] = useState<'create' | 'link'>('create');
  const [existingAppId, setExistingAppId] = useState('');
  const [lookedUp, setLookedUp] = useState<{ displayName: string } | null>(null);
  const [secretChoice, setSecretChoice] = useState<'generate' | 'manual'>('generate');
  const [manualSecret, setManualSecret] = useState('');
  /** Step 171 — Kai: "I can't choose the name for my application." Empty means
   *  "whatever the server would have named it". */
  const [appDisplayName, setAppDisplayName] = useState('');
  /** Publish this desk's roles as Entra app roles, so the enterprise
   *  application has something to assign people to. */
  const [publishRoles, setPublishRoles] = useState(true);
  /** Require an Entra assignment to sign in. Off by default, and the copy says
   *  why: switching it on before anyone is assigned locks everybody out. */
  const [requireAssignment, setRequireAssignment] = useState(false);
  /** Step 171 — one Microsoft sign-in, not two. See the copy below. */
  const [alsoMail, setAlsoMail] = useState(true);
  const [steps, setSteps] = useState<StepResult[] | null>(null);
  const [pass, setPass] = useState(false);
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep('signin');
    setSteps(null);
    setError(null);
    void (async () => {
      const res = await apiSend<{
        sessionId: string;
        userCode: string;
        verificationUri: string;
        interval: number;
      }>('/api/admin/sso-settings/ms/start', 'POST', { scope });
      if (!res.ok) return setError(res.error);
      setSession(res.body!);
      pollTimer.current = setInterval(
        async () => {
          const poll = await apiFetch<{ status: string; plan?: { tenantName: string }; message?: string }>(
            `/api/admin/sso-settings/ms/${res.body!.sessionId}/poll`,
          ).catch(() => null);
          if (!poll) return;
          if (poll.status === 'ready') {
            if (pollTimer.current) clearInterval(pollTimer.current);
            setTenantName(poll.plan?.tenantName ?? '');
            setStep('app');
          } else if (poll.status === 'failed') {
            if (pollTimer.current) clearInterval(pollTimer.current);
            setError(poll.message ?? 'Sign-in failed');
          }
        },
        Math.max((res.body!.interval ?? 5) * 1000, 3000),
      );
    })();
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [open, scope]);

  async function lookup() {
    setBusy(true);
    setError(null);
    setLookedUp(null);
    try {
      setLookedUp(
        await apiFetch<{ displayName: string }>(
          `/api/admin/sso-settings/ms/${session!.sessionId}/lookup?appId=${encodeURIComponent(existingAppId.trim())}`,
        ),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function provision() {
    setBusy(true);
    setStep('provision');
    const res = await apiSend<{ pass: boolean; steps: StepResult[] }>(
      `/api/admin/sso-settings/ms/${session!.sessionId}/provision`,
      'POST',
      {
        scope,
        mode,
        existingAppId: mode === 'link' ? existingAppId.trim() : undefined,
        generateNewSecret: secretChoice === 'generate',
        manualSecret: secretChoice === 'manual' ? manualSecret : undefined,
        appDisplayName: mode === 'create' && appDisplayName.trim() ? appDisplayName.trim() : undefined,
        publishRoles,
        requireAssignment,
        alsoMail,
        alsoDirectory: alsoMail,
      },
    );
    setBusy(false);
    if (!res.ok) return setError(res.error);
    setSteps(res.body!.steps);
    setPass(res.body!.pass);
  }

  async function activate() {
    setBusy(true);
    const res = await apiSend('/api/admin/sso-settings/activate', 'POST', { scope, isActive: true });
    setBusy(false);
    if (!res.ok) return setError(res.error);
    void queryClient.invalidateQueries({ queryKey: qk.ssoSettings });
    void queryClient.invalidateQueries({ queryKey: qk.branding }); // login button lights up
    /**
     * And the mail settings, because they carry the sign-in application's
     * client ID so the mail wizard can offer to reuse it.
     *
     * Kai hit this on the live install: sign-in configured, green tick, and
     * the mail wizard still had no reuse option. Queries here are stale after
     * 30 seconds, and the setup wizard runs sign-in and outbound mail
     * back-to-back — so the mail step served a cached answer from before
     * sign-in existed, every time, for everybody. A derived field is only as
     * fresh as the query that carries it.
     */
    void queryClient.invalidateQueries({ queryKey: qk.mailSettings });
    void queryClient.invalidateQueries({ queryKey: qk.setupMailStored });
    onClose(true);
  }

  return (
    <Modal open={open} onClose={() => onClose(false)} title={`Microsoft sign-in setup — ${scope}`} wide>
      {step === 'signin' && (
        <div className="space-y-3 text-center">
          {session ? (
            <>
              <p className="text-sm text-muted-foreground">
                Enter this code on the Microsoft page, signing in as someone who can create app registrations.
              </p>
              <div className="mx-auto flex w-fit items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2">
                <span className="font-mono text-2xl font-bold tracking-widest">{session.userCode}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Copy code"
                  onClick={() => void navigator.clipboard.writeText(session.userCode).catch(() => {})}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <Button onClick={() => window.open(session.verificationUri, '_blank', 'noopener')}>
                <ExternalLink /> Open microsoft.com/devicelogin
              </Button>
              <p className="text-xs text-muted-foreground">Waiting for the sign-in…</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">Starting…</p>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      )}

      {step === 'app' && (
        <div className="space-y-3">
          {tenantName && (
            <p className="text-xs text-muted-foreground">
              Signed in to <span className="font-medium text-foreground">{tenantName}</span>.
            </p>
          )}
          <button
            className={cn(
              'w-full rounded-lg border p-3 text-left text-sm hover:bg-accent/50',
              mode === 'create' && 'border-brand ring-1 ring-brand',
            )}
            onClick={() => setMode('create')}
          >
            <span className="font-medium">Create a new app registration</span>
            <span className="block text-xs text-muted-foreground">
              Dedicated to agent sign-in. Redirect URI added automatically, and you choose the name.
            </span>
          </button>
          <button
            className={cn(
              'w-full rounded-lg border p-3 text-left text-sm hover:bg-accent/50',
              mode === 'link' && 'border-brand ring-1 ring-brand',
            )}
            onClick={() => setMode('link')}
          >
            <span className="font-medium">Use an existing app registration</span>
            <span className="block text-xs text-muted-foreground">
              Reusing the mail app is fine — the wizard adds the sign-in scopes and redirect URI to it.
            </span>
          </button>
          {mode === 'create' && (
            <div className="space-y-1.5">
              <label htmlFor="sso-app-name" className="text-sm font-medium">
                What should it be called in Microsoft?
              </label>
              <Input
                id="sso-app-name"
                placeholder="Service Desk Sign-in"
                value={appDisplayName}
                onChange={(e) => setAppDisplayName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                The name people will see in the Entra portal. Leave it empty and it is named after your
                service desk.
              </p>
            </div>
          )}
          <label className="flex items-start gap-2 rounded-lg border p-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={alsoMail}
              onChange={(e) => setAlsoMail(e.target.checked)}
            />
            <span>
              <span className="font-medium">Use this same application for mail and the staff directory</span>
              <span className="block text-xs text-muted-foreground">
                Adds permission to send and read the service desk mailbox, and to read the directory, groups
                and this application's own role assignments, to this one registration while you are already
                signed in. Outbound mail then needs nothing from Microsoft — just the mailbox address and a
                test send. Leave it off to keep sign-in and mail as separate registrations, and expect to sign
                in to Microsoft again.
              </span>
            </span>
          </label>
          <div className="space-y-2 rounded-lg border p-3">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={publishRoles}
                onChange={(e) => setPublishRoles(e.target.checked)}
              />
              <span>
                <span className="font-medium">Publish this desk's roles to Microsoft</span>
                <span className="block text-xs text-muted-foreground">
                  Creates an app role for each role here, so the enterprise application has something to
                  assign people to. Who can do what is still decided in this app, not in Entra.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={requireAssignment}
                disabled={!publishRoles}
                onChange={(e) => setRequireAssignment(e.target.checked)}
              />
              <span>
                <span className="font-medium">Require an assignment in Microsoft to sign in</span>
                <span className="block text-xs text-muted-foreground">
                  Leave this off for now. Switching it on before anyone has been assigned locks out everybody,
                  including you, and the way back in is the Entra portal. You can turn it on there once the
                  right people are assigned.
                </span>
              </span>
            </label>
          </div>
          {mode === 'link' && (
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Application (client) ID"
                  value={existingAppId}
                  onChange={(e) => {
                    setExistingAppId(e.target.value);
                    setLookedUp(null);
                  }}
                />
                <Button variant="outline" onClick={lookup} disabled={busy || !existingAppId.trim()}>
                  Look up
                </Button>
              </div>
              {lookedUp && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400">
                  Found: <span className="font-medium">{lookedUp.displayName}</span>
                </p>
              )}
              <div className="flex gap-1">
                {(['generate', 'manual'] as const).map((c) => (
                  <button
                    key={c}
                    onClick={() => setSecretChoice(c)}
                    className={cn(
                      'rounded-md px-2.5 py-1 text-xs font-medium',
                      secretChoice === c
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent',
                    )}
                  >
                    {c === 'generate' ? 'Generate a new secret' : 'I have a secret already'}
                  </button>
                ))}
              </div>
              {secretChoice === 'manual' && (
                <Input
                  type="password"
                  placeholder="Existing client secret"
                  value={manualSecret}
                  onChange={(e) => setManualSecret(e.target.value)}
                />
              )}
            </div>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex justify-end pt-1">
            <Button
              disabled={
                busy || (mode === 'link' && (!lookedUp || (secretChoice === 'manual' && !manualSecret)))
              }
              onClick={provision}
            >
              Set it up
            </Button>
          </div>
        </div>
      )}

      {step === 'provision' && (
        <div className="space-y-3">
          {!steps && <p className="text-sm text-muted-foreground">Talking to Microsoft…</p>}
          {steps && (
            <ul className="space-y-1.5">
              {steps.map((s) => (
                <li key={s.label} className="flex items-start gap-2 text-sm">
                  {s.pass ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                  ) : (
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  )}
                  <span>
                    {s.label}
                    {s.error && (
                      <span className="block break-all font-mono text-[11px] text-destructive/90">
                        {s.error}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {error && <p className="text-xs text-destructive">{error}</p>}
          {steps && (
            <div className="flex justify-end pt-1">
              <Button disabled={!pass} onClick={() => setStep('activate')}>
                {pass ? 'Next' : 'Fix the failed step to continue'}
              </Button>
            </div>
          )}
        </div>
      )}

      {step === 'activate' && (
        <div className="space-y-3">
          <p className="text-sm">
            Saved but <b>not switched on</b>. For sign-in, the test IS signing in: turn it on, then prove it
            in a private window before you sign out of this one — if it misbehaves, come back here and turn it
            off.
          </p>
          <p className="rounded-md border bg-muted/30 p-2 font-mono text-xs">/auth/sso/login</p>
          {error && <p className="text-xs text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onClose(false)}>
              Not yet
            </Button>
            <Button onClick={activate} disabled={busy}>
              {busy ? 'Turning on…' : 'Turn it on'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/**
 * When the client secret expires — and how close that is.
 *
 * An expired Entra secret breaks sign-in for every agent at once, with no
 * warning anywhere in the product. Thirty days is the point at which someone
 * can still book the work; seven is the point at which it is an incident.
 */
function SecretExpiry({ at }: { at: number }) {
  const days = Math.floor((at - Date.now()) / 86_400_000);
  const when = new Date(at).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const tone =
    days < 0
      ? 'text-destructive'
      : days <= 7
        ? 'text-destructive'
        : days <= 30
          ? 'text-amber-600 dark:text-amber-500'
          : 'text-muted-foreground';
  return (
    <p className={cn('mt-1 flex items-center gap-1.5 text-xs', tone)}>
      {days <= 30 && <AlertTriangle className="h-3.5 w-3.5 shrink-0" />}
      {days < 0
        ? `Secret EXPIRED on ${when} — sign-in will be failing.`
        : days === 0
          ? `Secret expires today (${when}).`
          : `Secret expires in ${days} day${days === 1 ? '' : 's'} (${when}).`}
    </p>
  );
}

export function SsoSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.ssoSettings,
    queryFn: () => apiFetch<SsoResponse>('/api/admin/sso-settings'),
  });
  // One organisation per deployment (2026-09-14): only the org row is edited.
  const scope = 'org' as const;

  const stored = useMemo(
    () => (scope === 'org' ? (data?.org ?? EMPTY) : (data?.global ?? EMPTY)),
    [data, scope],
  );
  const [draft, setDraft] = useState<SsoRow>(EMPTY);
  /** Step 155 — the agent-link toggle saves on click, so it needs its own
   *  in-flight flag rather than sharing the credential form's. */
  const [linkBusy, setLinkBusy] = useState(false);
  useEffect(() => setDraft(stored), [stored]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(stored);
  useDirty(dirty);
  const [wizardOpen, setWizardOpen] = useState(false);

  const save = useMutation({
    mutationFn: () => apiSend('/api/admin/sso-settings', 'PUT', { scope, ...draft }),
    onSuccess: (res) => {
      if (!res.ok) return;
      void queryClient.invalidateQueries({ queryKey: qk.ssoSettings });
      void queryClient.invalidateQueries({ queryKey: qk.branding });
    },
  });

  if (isPending)
    return (
      <div className="max-w-xl space-y-3 p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-56 w-full" />
      </div>
    );
  if (isError)
    return (
      <div className="m-6 max-w-xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load sign-in settings</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  const set = <K extends keyof SsoRow>(k: K, v: SsoRow[K]) => setDraft((d) => ({ ...d, [k]: v }));

  return (
    <div className="max-w-xl p-6">
      <div className="mb-1 flex items-center gap-3">
        <h1 className="text-lg font-semibold">Sign-in (Microsoft)</h1>
        <Button size="sm" variant="outline" onClick={() => setWizardOpen(true)}>
          <Wand2 /> Set up with the wizard
        </Button>
        {data?.effectiveScope ? (
          <span className="rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs text-emerald-600 dark:text-emerald-400">
            active via {data.effectiveScope === 'org' ? 'this organisation' : 'global'}
          </span>
        ) : (
          <span className="rounded-md bg-amber-500/15 px-2 py-0.5 text-xs text-amber-600 dark:text-amber-400">
            off
          </span>
        )}
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        Agent sign-in with Microsoft Entra. Global is the default; an organisation row overrides it.
        Requesters keep using magic links either way.
      </p>

      <div className="space-y-3 rounded-lg border bg-card p-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Entra tenant ID</label>
            <Input
              value={draft.entraTenantId ?? ''}
              onChange={(e) => set('entraTenantId', e.target.value || null)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Client ID</label>
            <Input value={draft.clientId ?? ''} onChange={(e) => set('clientId', e.target.value || null)} />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Client secret</label>
          <Input
            type="password"
            value={draft.clientSecret ?? ''}
            onChange={(e) => set('clientSecret', e.target.value || null)}
            placeholder="leave the dots untouched to keep the stored secret"
          />
          {/* Step 9 recorded the expiry so a secret could not die silently;
              the field was returned by the API and never rendered, so the
              only symptom of expiry was sign-in breaking. Surfaced here, and
              warned on BEFORE it happens rather than after. */}
          {draft.secretExpiresAt != null && <SecretExpiry at={draft.secretExpiresAt} />}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Redirect URI (register this on the app)
          </label>
          <p className="rounded-md border bg-muted/30 p-2 font-mono text-xs">{data!.callbackUrl}</p>
        </div>
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" />
          <label className="text-xs text-muted-foreground">
            <input
              type="checkbox"
              className="mr-1.5 align-middle"
              checked={draft.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
            />
            Use these settings (the Microsoft button appears on the login page)
          </label>
        </div>
        {save.data && !save.data.ok && <p className="text-xs text-destructive">{save.data.error}</p>}
        <div className="flex items-center justify-end gap-3 border-t pt-3">
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          <Button variant="ghost" disabled={!dirty || save.isPending} onClick={() => setDraft(stored)}>
            Discard
          </Button>
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>
      {/* Step 155 — sign-in for a tenant with no Microsoft 365.

          Its own card with an immediate toggle, deliberately outside the
          credential form above: that form is a draft-first set of Entra
          fields, and the tenant this setting exists for will never fill any of
          them in. Burying the only way to let their agents in inside a form
          they cannot complete would make it unreachable. */}
      <div className="mt-6 rounded-lg border bg-card p-4">
        <h2 className="text-sm font-semibold">Sign-in links for agents</h2>
        <p className="mb-3 mt-0.5 text-sm text-muted-foreground">
          For organisations without Microsoft 365. Agents ask for a link on the sign-in page and it arrives by
          email — the same way requesters already sign in, and it needs nothing from Microsoft.
        </p>
        <label className="flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-input"
            checked={!!stored.agentLinkSignIn}
            disabled={linkBusy}
            onChange={async (e) => {
              setLinkBusy(true);
              await apiSend('/api/admin/sso-settings/agent-link', 'POST', {
                scope,
                enabled: e.target.checked,
              });
              setLinkBusy(false);
              void queryClient.invalidateQueries({ queryKey: qk.ssoSettings });
              // The login page reads this from branding, so it has to be
              // refetched or the card does not appear until a reload.
              void queryClient.invalidateQueries({ queryKey: qk.branding });
            }}
          />
          <span>
            <span className="font-medium">Let agents sign in with an emailed link</span>
            {/* Said plainly rather than left to be discovered: a link is only
                as strong as the mailbox, and an admin turning this on for a
                tenant that HAS Entra is adding a door around its MFA. */}
            <span className="block text-xs text-muted-foreground">
              A link is as strong as the mailbox it arrives in. If your agents sign in with Microsoft, leave
              this off — it bypasses any MFA or conditional access you have there.
            </span>
          </span>
        </label>
      </div>

      <SsoWizard scope={scope} open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </div>
  );
}
